import type { ReactNode } from "react";

import { AdminIcon, type IconName } from "@/components/admin/icon";
import { Popover } from "@/components/admin/popover";

export interface ActionMenuItem {
	label: string;
	icon?: IconName;
	/** Action destructrice : libellé en rouge, et placée après un séparateur. */
	danger?: boolean;
	disabled?: boolean;
	onClick: () => void;
}

/** Trait de séparation entre deux groupes d'actions. */
export type ActionMenuEntry = ActionMenuItem | "sep";

/**
 * Menu d'actions d'une ligne de tableau ou d'une carte.
 *
 * Le besoin revenait sur une quinzaine d'écrans, chacun le réimplémentant avec
 * ses propres coordonnées de positionnement — d'où des menus qui sortaient de
 * la fenêtre sur la dernière ligne. Celui-ci s'appuie sur `Popover`, qui gère
 * déjà le placement, la fermeture au clic extérieur, à Échap et au changement
 * de route.
 *
 * Avec `label`, le déclencheur est un bouton nommé ; sans, c'est le bouton à
 * trois points des tableaux.
 */
export function ActionMenu({
	items,
	label,
	align = "right",
	testId,
}: {
	items: ActionMenuEntry[];
	label?: ReactNode;
	align?: "left" | "right";
	testId?: string;
}) {
	const visibles = items.filter(
		(item, index) =>
			// Un séparateur en tête, en queue, ou doublé ne sépare rien.
			item !== "sep" ||
			(index > 0 && index < items.length - 1 && items[index - 1] !== "sep"),
	);

	if (visibles.filter((i) => i !== "sep").length === 0) return null;

	return (
		<Popover
			align={align}
			width={232}
			testId={testId}
			trigger={
				label ? (
					<button type="button" className="btn btn-ghost btn-sm">
						{label}
						<AdminIcon n="chevD" s={12} />
					</button>
				) : (
					<button
						type="button"
						className="top-btn"
						aria-label="Actions"
						title="Actions"
					>
						<AdminIcon n="dots" s={14} />
					</button>
				)
			}
		>
			{(close) => (
				<div style={{ padding: 4 }}>
					{visibles.map((item, index) =>
						item === "sep" ? (
							<div
								key={`sep-${index}`}
								style={{
									height: 1,
									background: "var(--a-border)",
									margin: "4px 0",
								}}
							/>
						) : (
							<button
								key={item.label}
								type="button"
								disabled={item.disabled}
								onClick={() => {
									close();
									item.onClick();
								}}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 9,
									width: "100%",
									padding: "8px 10px",
									borderRadius: 7,
									border: "none",
									background: "none",
									fontFamily: "inherit",
									fontSize: 12.5,
									textAlign: "left",
									cursor: item.disabled ? "default" : "pointer",
									opacity: item.disabled ? 0.45 : 1,
									color: item.danger ? "var(--a-danger)" : "var(--a-ink)",
								}}
								onMouseEnter={(e) => {
									if (!item.disabled) {
										e.currentTarget.style.background = "var(--a-surface-2)";
									}
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.background = "none";
								}}
							>
								{item.icon && (
									<AdminIcon n={item.icon} s={14} c="currentColor" />
								)}
								<span>{item.label}</span>
							</button>
						),
					)}
				</div>
			)}
		</Popover>
	);
}
